
import * as React from "react";
import { ChevronLeft, ChevronRight, Clock, Bell } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";

export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  reminders?: Array<{
    date: Date;
    title: string;
  }>;
  events?: Array<{
    date: Date;
    type: 'study' | 'exam' | 'break';
  }>;
};

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  reminders = [],
  events = [],
  ...props
}: CalendarProps) {
  // Function to check if a date has an event
  const hasEvent = (date: Date) => {
    return events.some(event => 
      event.date.getDate() === date.getDate() &&
      event.date.getMonth() === date.getMonth() &&
      event.date.getFullYear() === date.getFullYear()
    );
  };

  // Function to check if a date has a reminder
  const hasReminder = (date: Date) => {
    return reminders.some(reminder => 
      reminder.date.getDate() === date.getDate() &&
      reminder.date.getMonth() === date.getMonth() &&
      reminder.date.getFullYear() === date.getFullYear()
    );
  };

  // Function to get reminder details for a date
  const getReminderDetails = (date: Date) => {
    return reminders.find(reminder => 
      reminder.date.getDate() === date.getDate() &&
      reminder.date.getMonth() === date.getMonth() &&
      reminder.date.getFullYear() === date.getFullYear()
    );
  };

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn("p-3", className)}
      classNames={{
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption: "flex justify-center pt-1 relative items-center",
        caption_label: "text-sm font-medium text-[#8B5CF6]",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 hover:bg-[#8B5CF6]/10 border-[#8B5CF6]/20"
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell: "text-[#8B5CF6] rounded-md w-9 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: cn(
          "relative h-9 w-9 text-center text-sm p-0 [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
          "hover:bg-[#8B5CF6]/5 transition-colors"
        ),
        day: cn(
          buttonVariants({ variant: "ghost" }),
          "h-9 w-9 p-0 font-normal aria-selected:opacity-100 hover:bg-[#8B5CF6]/10"
        ),
        day_range_end: "day-range-end",
        day_selected: "bg-[#8B5CF6] text-white hover:bg-[#7C4DEF] hover:text-white focus:bg-[#8B5CF6] focus:text-white",
        day_today: "bg-[#8B5CF6]/10 text-[#8B5CF6]",
        day_outside: "text-gray-400 opacity-50",
        day_disabled: "text-gray-400 opacity-50",
        day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: ({ ..._props }) => <ChevronLeft className="h-4 w-4 text-[#8B5CF6]" />,
        IconRight: ({ ..._props }) => <ChevronRight className="h-4 w-4 text-[#8B5CF6]" />,
        DayContent: ({ date, ...props }) => (
          <div {...props} className="relative w-full h-full flex items-center justify-center">
            <span>{date.getDate()}</span>
            {hasEvent(date) && (
              <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2">
                <div className="w-1 h-1 rounded-full bg-[#8B5CF6]" />
              </div>
            )}
            {hasReminder(date) && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger>
                    <Bell className="absolute top-0 right-0 w-3 h-3 text-[#8B5CF6]" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{getReminderDetails(date)?.title || "Reminder set"}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        ),
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
